import { PlatformRef } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { SystemCriticalError } from '@trustedshops/tswp-carrier-core';
import { ErrorCode } from '@trustedshops/tswp-carrier-core';
import { BootstrapErrorModule } from './bootstrap-error.module';

// Type-safe global storage for bootstrap error
export interface WindowWithBootstrapError extends Window {
  __bootstrapError?: SystemCriticalError | null;
}

const SUPPORT_URL = 'https://help.etrusted.com/';
const ERROR_ELEMENT_SELECTOR = 'app-bootstrap-error';
const PRESENTATION_ELEMENT_SELECTOR = 'presentation';
const LOADING_CONTAINER_ID = 'loading-container';

/**
 * Creates a fallback HTML error message when Angular bootstrap fails completely
 */
function createFallbackErrorMessage(): string {
  return `
    <div style="display: flex; align-items: center; justify-content: center; height: 100vh; flex-direction: column; font-family: Arial, sans-serif; padding: 2rem;">
      <h1>Application Error</h1>
      <p>An unexpected error occurred during application startup.</p>
      <p>Please contact support.</p>
      <a href="${SUPPORT_URL}" target="_blank" style="margin-top: 1rem; color: #007bff; text-decoration: none;">Contact Support</a>
    </div>
  `;
}

/**
 * Checks if a value is an error-like object (has error properties)
 * This handles cases where objects implement Error interface but don't extend Error class
 */
function isErrorLike(error: unknown): error is { message: string; stack?: string; name?: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as any).message === 'string'
  );
}

/**
 * Creates a SystemCriticalError from any error type
 */
function createSystemCriticalError(error: unknown): SystemCriticalError {
  let errorInstance: Error;

  if (error instanceof Error) {
    errorInstance = error;
  } else if (isErrorLike(error)) {
    // Error-like object (e.g., HttpErrorResponse) that implements Error interface
    // but doesn't extend Error class
    errorInstance = new Error(error.message);
    // Preserve stack trace if available
    if (error.stack) {
      errorInstance.stack = error.stack;
    }
    // Preserve name if available
    if (error.name) {
      errorInstance.name = error.name;
    }
  } else {
    errorInstance = new Error(String(error));
  }

  return new SystemCriticalError(errorInstance, ErrorCode.BootstrapError);
}

/**
 * Stores the bootstrap error globally for ErrorService to access
 */
function storeBootstrapError(error: SystemCriticalError): void {
  (window as WindowWithBootstrapError).__bootstrapError = error;
}

/**
 * Prepares the DOM for the error component by replacing presentation element
 */
function prepareErrorComponentElement(): void {
  const presentationElement = document.querySelector(PRESENTATION_ELEMENT_SELECTOR);
  const errorElement = document.createElement(ERROR_ELEMENT_SELECTOR);

  if (presentationElement?.parentNode) {
    presentationElement.parentNode.replaceChild(errorElement, presentationElement);
  } else {
    document.body.appendChild(errorElement);
  }
}

/**
 * Hides the loading container if it exists
 */
function hideLoadingContainer(): void {
  const loadingContainer = document.getElementById(LOADING_CONTAINER_ID);
  if (loadingContainer) {
    loadingContainer.style.display = 'none';
  }
}

/**
 * Bootstraps the error fallback module
 */
function bootstrapErrorModule(platform: PlatformRef): Promise<void> {
  return platform.bootstrapModule(BootstrapErrorModule)
    .then(() => {
      // Discard the NgModuleRef result to match Promise<void> return type
    })
    .catch((fallbackError) => {
      console.error('[BootstrapErrorHandler] Failed to load error fallback component', fallbackError);
      document.body.innerHTML = createFallbackErrorMessage();
      throw fallbackError;
    });
}

/**
 * Initializes the global error storage
 */
export function initializeBootstrapErrorStorage(): void {
  (window as WindowWithBootstrapError).__bootstrapError = null;
}

/**
 * Handles bootstrap failure by showing error component or fallback message
 */
export function handleBootstrapError(error: unknown): void {
  console.error('[BootstrapErrorHandler] Bootstrap failed', error);
  if (error instanceof Error || isErrorLike(error)) {
    const stack = error instanceof Error ? error.stack : (error as any).stack;
    if (stack) {
      console.error('[BootstrapErrorHandler] Bootstrap error stack:', stack);
    }
  }

  try {
    const systemError = createSystemCriticalError(error);
    storeBootstrapError(systemError);
    prepareErrorComponentElement();
    hideLoadingContainer();

    const errorPlatform = platformBrowserDynamic();
    bootstrapErrorModule(errorPlatform);
  } catch (bootstrapError) {
    console.error('[BootstrapErrorHandler] Failed to initialize error handling', bootstrapError);
    document.body.innerHTML = createFallbackErrorMessage();
  }
}
